<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: additional fields -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Name Input -->
              <base-input
                col="6"
                type="text"
                :placeholder="$t('PLACEHOLDERS.name')"
                v-model.trim="filterOptions.name"
              />
              <!-- End:: Name Input -->

              <!-- Start:: estate_types Input -->
              <base-select-input
                col="6"
                :optionsList="estate_types"
                :placeholder="$t('PLACEHOLDERS.estate_type')"
                v-model="filterOptions.estate_types"
              />
              <!-- End:: estate_types Input -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("PLACEHOLDERS.additional_fields") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
        <div
          class="title_route_wrapper"
          v-if="$can('additional fields create', 'additional-fields')"
        >
          <router-link to="/additional-fields/create">
            {{ $t("PLACEHOLDERS.add_additional_field") }}
          </router-link>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->
      <div class="d-flex justify-content-between">
        <v-col
          cols="auto"
          md="auto"
          sm="auto"
          class="d-flex justify-center my-4 gap-2"
        >
          <v-btn
            v-if="selected.length"
            @click="openDeleteDialogForSelected"
            size="x-large"
            color="error"
          >
            {{ $t("PLACEHOLDERS.delete_selected") }}
          </v-btn>
        </v-col>
        <base-input
          :label="$t('TITLES.item_per_page')"
          :placeholder="$t('TITLES.item_per_page')"
          col="5"
          type="number"
          class="my-0 base-input py-0"
          v-model="paginations.items_per_page"
        />
      </div>
      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        v-model="selected"
        show-select
        item-class="ltr"
        :items-per-page="
          paginations.items_per_page === ''
            ? 10
            : Number(paginations.items_per_page)
        "
        hide-default-footer
      >
        <template v-slot:header.data-table-select>
          <v-checkbox
            :disabled="tableRows.every((row) => row.can_remove === false)"
            v-model="myHeaderCheck"
            @click.stop="handleHeaderClick"
          />
        </template>

        <template v-slot:item.data-table-select="{ isSelected, select, item }">
          <v-checkbox
            :input-value="isSelected"
            @change="select($event)"
            color="primary"
            hide-details
            :disabled="!item.can_remove"
            dense
          />
        </template>
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- Start:: No Data State -->

        <template v-slot:[`item.id`]="{ item, index }">
          <p class="blue-grey--text text--darken-1 fs-3" v-if="!item.id">-</p>
          <p v-else>
            {{
              (paginations.current_page - 1) * paginations.items_per_page +
              index +
              1
            }}
          </p>
        </template>
        <!-- Start:: Activation -->
        <template v-slot:[`item.is_active`]="{ item }">
          <div
            class="activation"
            dir="ltr"
            style="z-index: 1"
            v-if="$can('additional fields activate', 'additional-fields')"
          >
            <v-switch
              class="mt-2"
              color="success"
              v-model="item.is_active"
              hide-details
              @change="changeActivationStatus(item)"
            ></v-switch>
          </div>

          <template v-else>
            <span class="text-success text-h5" v-if="item.is_active">
              <i class="far fa-check"></i>
            </span>
            <span class="text-danger text-h5" v-else>
              <i class="far fa-times"></i>
            </span>
          </template>
        </template>
        <!-- End:: Activation -->

        <!-- Start:: is_required -->
        <template v-slot:[`item.is_required`]="{ item }">
          <div v-if="item.is_required">{{ $t("PLACEHOLDERS.required") }}</div>

          <div v-else>{{ $t("PLACEHOLDERS.not_required") }}</div>
        </template>
        <!-- End:: is_required -->

        <!-- Start:: estate_types -->
        <template v-slot:[`item.estate_types`]="{ item }">
          <div v-if="item.estate_types?.length > 1">
            {{ item.estate_types?.map((type) => type.name).join(" & ") }}
          </div>

          <div v-if="item.estate_types?.length == 1">
            {{ item.estate_types[0]?.name }}
          </div>

          <div v-if="item.estate_types?.length == 0">-</div>
        </template>
        <!-- End:: estate_types -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <a-tooltip
              placement="bottom"
              v-if="$can('additional fields show', 'additional-fields')"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="showItem(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>

            <a-tooltip
              placement="bottom"
              v-if="$can('additional fields edit', 'additional-fields')"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.edit") }}</span>
              </template>
              <button class="btn_edit" @click="editItem(item)">
                <i class="fal fa-edit"></i>
              </button>
            </a-tooltip>

            <a-tooltip
              placement="bottom"
              v-if="$can('additional fields delete', 'additional-fields')"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.delete") }}</span>
              </template>
              <button class="btn_delete" @click="selectDeleteItem(item)">
                <i class="fal fa-trash-alt"></i>
              </button>
            </a-tooltip>

            <template v-else>
              <i
                class="fal fa-lock-alt fs-5 blue-grey--text text--darken-1"
              ></i>
            </template>
          </div>
        </template>
        <!-- End:: Actions -->

        <!-- ======================== Start:: Dialogs ======================== -->
        <template v-slot:top>
          <!-- Start:: Delete Modal -->
          <v-dialog v-model="dialogDelete">
            <v-card>
              <v-card-title class="text-h5 justify-center" v-if="itemToDelete">
                {{
                  $t("TITLES.DeleteConfirmingMessage", {
                    name: itemToDelete.name,
                  })
                }}
              </v-card-title>
              <v-card-title class="text-h5 justify-center">
                {{ $t("TITLES.delete_selected") }}
              </v-card-title>
              <v-card-actions>
                <v-btn
                  class="modal_confirm_btn"
                  @click="
                    !itemToDelete
                      ? confirmDeleteSelected()
                      : confirmDeleteItem()
                  "
                  >{{ $t("BUTTONS.ok") }}</v-btn
                >

                <v-btn class="modal_cancel_btn" @click="dialogDelete = false">{{
                  $t("BUTTONS.cancel")
                }}</v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- change status -->
          <v-dialog v-model="changeStatus">
            <v-card>
              <v-card-title class="text-h5 justify-center">
                {{ $t("TITLES.chane_selected") }}
              </v-card-title>
              <v-card-actions>
                <v-btn
                  class="modal_confirm_btn"
                  @click="confirmChangeSelectedStatus"
                  >{{ $t("BUTTONS.ok") }}</v-btn
                >

                <v-btn class="modal_cancel_btn" @click="changeStatus = false">{{
                  $t("BUTTONS.cancel")
                }}</v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- End:: Delete Modal -->
        </template>
        <!-- ======================== End:: Dialogs ======================== -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: additional fields -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { debounce } from "lodash";
import { mapGetters } from "vuex";

export default {
  name: "AllAreas",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    activeStatuses() {
      return [
        {
          id: 1,
          name: this.$t("STATUS.active"),
          value: 1,
        },
        {
          id: 2,
          name: this.$t("STATUS.notActive"),
          value: false,
        },
      ];
    },
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data
      changeStatus: false,
      myHeaderCheck: false,

      selected: [],
      estate_types: [],

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        name: null,
        estate_types: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "id",
          align: "center",
          width: "80",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.name"),
          value: "name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.estate_type"),
          value: "estate_types",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.is_required"),
          value: "is_required",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.status"),
          value: "is_active",
          align: "center",
          sortable: false,
          width: "120",
        },
        {
          text: this.$t("TABLES.Admins.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Dialogs Control Data
      dialogDelete: false,
      itemToDelete: null,
      // End:: Dialogs Control Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
      // End:: Pagination Data

      // Start:: Page Permissions
      permissions: null,
      // Start:: Page Permissions

      allAdditionalFields: [],
    };
  },

  watch: {
    // Start:: Page Query Param Watcher To Get Page Data Based On It's Change
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.myHeaderCheck = false;
      this.selected = [];
      this.setTableRows();
    },
    "paginations.items_per_page"(newVal, oldVal) {
      this.paginations.current_page = 1;
      this.debouncedFetch();
    },
    selected(data) {
      if (data.length == 0) {
        this.myHeaderCheck = false;
      } else {
        this.myHeaderCheck = true;
      }
    },
    // End:: Page Query Param Watcher To Get Page Data Based On It's Change
  },

  methods: {
    async confirmDeleteSelected() {
      const REQUEST_DATA = new FormData();
      REQUEST_DATA.append("_method", "DELETE");
      this.selected.map((item, index) => {
        REQUEST_DATA.append(`ids[${index}]`, `${item.id}`);
      });
      try {
        await this.$axios({
          method: "POST",
          url: `additional-fields/remove-many`,
          data: REQUEST_DATA,
        });
        this.dialogDelete = false;
        this.selected = [];
        this.myHeaderCheck = false;
        this.setTableRows();
        this.$message.success(this.$t("MESSAGES.delete_selected_successfully"));
      } catch (error) {
        this.dialogDelete = false;
        this.$message.error(error.response.data.message);
      }
    },

    handleHeaderClick() {
      if (this.myHeaderCheck) {
        this.selected = this.tableRows.filter((item) => item.can_remove);
      } else {
        this.selected = [];
      }
    },

    toggleSelectAll() {
      if (this.selected.length === this.tableRows.length) {
        this.selected = [];
      } else {
        this.selected = [...this.tableRows];
      }
    },
    debouncedFetch: debounce(function () {
      this.setTableRows();
    }, 500),
    // Start:: Handel Filter
    openDeleteDialogForSelected() {
      this.itemToDelete = null;
      this.dialogDelete = true;
    },

    async confirmChangeSelectedStatus() {
      console.log(this.selected);
    },
    // Start:: Handel Filter
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/additional-fields/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.name = null;
      this.filterOptions.estate_types = null;
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/additional-fields/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    // End:: Handel Filter

    // Start:: Set Table Rows
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      // Scroll To Screen's Top After Get Products
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },
    async setTableRows() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "additional-fields",
          params: {
            page: this.paginations.current_page,
            name: this.filterOptions.name,
            estate_type: this.filterOptions.estate_types?.id,
            limit:
              this.paginations.items_per_page === ""
                ? 10
                : Number(this.paginations.items_per_page),
          },
        });
        this.loading = false;
        this.tableRows = res.data.data;
        this.paginations.last_page = res.data.meta.last_page;
      } catch (error) {
        this.loading = false;
        console.log(error.response.data.message);
      }
    },
    // End:: Set Table Rows

    // ==================== Start:: Crud ====================
    // ===== Start:: Edit
    editItem(item) {
      this.$router.push({ path: `/additional-fields/edit/${item.id}` });
    },
    showItem(item) {
      this.$router.push({ path: `/additional-fields/show/${item.id}` });
    },
    // ===== End:: Edit

    // ===== Start:: Delete
    selectDeleteItem(item) {
      this.dialogDelete = true;
      this.itemToDelete = item;
    },
    async confirmDeleteItem() {
      try {
        await this.$axios({
          method: "DELETE",
          url: `additional-fields/${this.itemToDelete.id}`,
        });
        this.dialogDelete = false;
        this.setTableRows();
        this.$message.success(this.$t("MESSAGES.deletedSuccessfully"));
      } catch (error) {
        this.dialogDelete = false;
        this.$message.error(error.response.data.message);
      }
    },
    // ===== End:: Delete
    // ==================== End:: Crud ====================

    // Start:: Change Activation Status
    async changeActivationStatus(item) {
      const REQUEST_DATA = new FormData();
      // REQUEST_DATA.append("is_active", +item.status == 1 ? 0 : 1);
      try {
        let response = await this.$axios({
          method: "POST",
          url: `additional-fields/activate/${item.id}`,
        });
        this.setTableRows();
        this.$message.success(this.$t("MESSAGES.changedSuccessfully"));
      } catch (error) {
        this.$message.error(error.response.data.message);
      }
    },
    // End:: Change Activation Status

    async getEstateTypes() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: `estate-types?page=0&limit=0&status=1`,
        });
        this.estate_types = res.data.data;
      } catch (error) {
        console.log(error.response.data.message);
      }
    },
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.getEstateTypes();
    this.setTableRows();
    // End:: Fire Methods
  },
};
</script>
